import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { IFieldProps } from "types/Organisational";

import { useOrganisation } from "@RequestFormV2/sections/Organisation/context/useOrganisation";
import { mapDropdownOptions } from "@RequestFormV2/sections/Organisation/hooks/organisationFieldOptions";

const EXCLUDED_FIELD_IDS = new Set(["shipment_title", "invoice_location", "custom_long_field_01", "cost_recovery_required"]);

const isExcludedField = (fieldId: string) =>
  EXCLUDED_FIELD_IDS.has(fieldId) || fieldId.startsWith("custom_cost_recovery_");

export function useOrganisationCustomFields() {
  const { organisationFields } = useOrganisation();
  const { setValue, getValues } = useFormContext();
  const [disabledFields, setDisabledFields] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setDisabledFields({});
  }, [organisationFields]);

  const [reasonCodeGroup] = useWatch({
    name: ["organizational.reason_code_group"],
  });

  const visibleFields = useMemo(() => {
    if (!organisationFields?.length) return [];
    return organisationFields.filter((field: IFieldProps) => !isExcludedField(field.field_id));
  }, [organisationFields]);

  const getDropdownOptions = (field: IFieldProps) => {
    const { field_id, dropdown_values } = field;
    if (!dropdown_values?.length) return [];

    if (field_id === "reason_code") {
      const hasReasonCodeGroup = organisationFields?.some(
        (f: IFieldProps) => f.field_id === "reason_code_group" && f.dropdown_values?.length,
      );
      if (hasReasonCodeGroup) {
        if (!reasonCodeGroup) return [];
        return (dropdown_values as any[])
          .filter((item) => item.group_name === reasonCodeGroup)
          .map((item) => ({ label: item.key, value: item.value }));
      }
    }

    if (field_id === "cost_center") {
      return (dropdown_values as any[]).map((item) => ({
        label: item.key.replace(/\n/g, ""),
        value: item.value,
      }));
    }

    return mapDropdownOptions(field);
  };

  const handleConditionalSelect = (newValue: string | null, field: IFieldProps) => {
    const { field_id, dropdown_values } = field;
    if (!dropdown_values) return;
    const previousValue = getValues(`organizational.${field_id}`);
    const previousOption = (dropdown_values as any[]).find((opt) => opt.value === previousValue);
    const newOption = (dropdown_values as any[]).find((opt) => opt.value === newValue);

    previousOption?.updates?.forEach((update: any) => {
      if (!update.selector?.startsWith("organizational.")) return;
      setValue(update.selector, null);
      if (update.disable) setDisabledFields((prev) => ({ ...prev, [update.selector]: false }));
    });
    newOption?.updates?.forEach((update: any) => {
      if (!update.selector?.startsWith("organizational.")) return;
      setValue(update.selector, update.value);
      if (update.disable) setDisabledFields((prev) => ({ ...prev, [update.selector]: true }));
    });
  };

  const isFieldDisabled = (fieldId: string) => !!disabledFields[`organizational.${fieldId}`];

  return { visibleFields, getDropdownOptions, handleConditionalSelect, isFieldDisabled, setValue };
}
